import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Mail, Phone, Shield, User as UserIcon, Flame } from 'lucide-react';
import { Button } from './ui/button';

const flameStyles = `
  @keyframes modalFireSparks {
    0% { transform: translateY(0) scale(1); opacity: 1; }
    100% { transform: translateY(-80px) scale(0); opacity: 0; }
  }

  .modal-spark {
    position: absolute;
    width: 2.5px;
    height: 2.5px;
    border-radius: 50%;
    pointer-events: none;
    z-index: 50;
    animation: modalFireSparks 1.2s linear forwards;
  }

  .flame-modal-container {
    perspective: 1200px;
  }

  .flame-modal-card {
    transition: transform 0.1s ease-out;
    transform-style: preserve-3d;
    background: black;
    border: 1px solid #FF4D00;
    box-shadow: 0 0 40px rgba(0, 0, 0, 0.8), 0 0 20px rgba(255, 77, 0, 0.15);
  }

  .modal-spotlight {
    position: absolute;
    inset: 0;
    background: radial-gradient(circle 300px at var(--x) var(--y), rgba(255, 77, 0, 0.06), transparent 80%);
    pointer-events: none;
    z-index: 10;
  }

  .premium-text {
    font-family: 'Inter', sans-serif;
  }

  .label-text {
    font-size: 9px;
    font-weight: 700;
    letter-spacing: 0.15em;
    text-transform: uppercase;
    color: #94a3b8;
  }

  .value-text {
    font-size: 14px;
    font-weight: 600;
    color: #f1f5f9;
  }
`;

const ProfileModal = ({ isOpen, onClose, user }) => {
    const [tilt, setTilt] = useState({ x: 0, y: 0, spotX: 0, spotY: 0 });
    const [sparks, setSparks] = useState([]);

    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') onClose();
        };
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            window.addEventListener('keydown', handleEsc);
        }
        return () => {
            document.body.style.overflow = 'unset';
            window.removeEventListener('keydown', handleEsc);
        };
    }, [isOpen, onClose]);

    if (!isOpen || !user) return null;

    const displayName = user?.name || 'User Name';
    const displayRole = user?.designation || user?.role || 'Member';

    const handleMouseMove = (e) => {
        const card = e.currentTarget;
        const rect = card.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        const centerX = rect.width / 2;
        const centerY = rect.height / 2;

        const rotateX = (y - centerY) / 40;
        const rotateY = (centerX - x) / 40;

        setTilt({ x: rotateX, y: rotateY, spotX: x, spotY: y });

        if (Math.random() > 0.9) {
            const id = Math.random();
            const sparkX = Math.random() * rect.width;
            setSparks(prev => [...prev.slice(-5), { id, x: sparkX, y: rect.height - 10, color: '#FF4D00' }]);
            setTimeout(() => setSparks(prev => prev.filter(s => s.id !== id)), 1200);
        }
    };

    const handleMouseLeave = () => {
        setTilt({ x: 0, y: 0, spotX: 0, spotY: 0 });
    };

    const modalContent = (
        <div
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-300"
            onClick={onClose}
        >
            <style>{flameStyles}</style>

            <div className="flame-modal-container w-full max-w-[380px]" onClick={(e) => e.stopPropagation()}>
                <div
                    onMouseMove={handleMouseMove}
                    onMouseLeave={handleMouseLeave}
                    style={{
                        transform: `rotateX(${tilt.x}deg) rotateY(${tilt.y}deg)`,
                        '--x': `${tilt.spotX}px`,
                        '--y': `${tilt.spotY}px`,
                        filter: 'none'
                    }}
                    className="flame-modal-card rounded-[2rem] overflow-hidden relative group premium-text"
                >
                    {/* Spotlight */}
                    <div className="modal-spotlight"></div>

                    {/* Sparks */}
                    {sparks.map(spark => (
                        <div
                            key={spark.id}
                            className="modal-spark"
                            style={{
                                left: spark.x,
                                top: spark.y,
                                backgroundColor: spark.color,
                                boxShadow: `0 0 6px ${spark.color}`
                            }}
                        />
                    ))}

                    {/* Close Button */}
                    <button
                        onClick={onClose}
                        className="absolute top-5 right-5 p-1.5 bg-white/5 hover:bg-white/10 rounded-full transition-all z-[60] border border-white/5"
                    >
                        <X size={18} className="text-zinc-500 hover:text-white transition-colors" />
                    </button>

                    {/* Header */}
                    <div className="h-32 bg-gradient-to-b from-[#FF4D00]/10 to-transparent w-full relative flex items-center justify-center border-b border-white/5">
                        <div className="relative mt-8">
                            <div className="w-24 h-24 rounded-full border-2 border-[#FF4D00] bg-black flex items-center justify-center shadow-[0_0_25px_rgba(255,77,0,0.2)] overflow-hidden relative z-20">
                                {user.avatar_url || user.photo_url ? (
                                    <img
                                        src={user.avatar_url || user.photo_url}
                                        alt={displayName}
                                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                                        style={{
                                            imageRendering: '-webkit-optimize-contrast',
                                            backfaceVisibility: 'hidden',
                                            transform: 'translateZ(1px)', // Force hardware acceleration and prevent clipping
                                            willChange: 'transform'
                                        }}
                                    />
                                ) : (
                                    <UserIcon size={40} className="text-zinc-800" />
                                )}
                            </div>
                            <Flame size={28} className="absolute -top-3 -right-1 text-[#FF4D00]/30 rotate-12 animate-pulse" />
                        </div>
                    </div>

                    {/* Content */}
                    <div className="pt-10 pb-10 px-8 text-center relative z-20">
                        <h2 className="text-2xl font-bold text-white mb-1.5 tracking-tight">{displayName}</h2>
                        <div className="flex items-center justify-center gap-2 mb-8">
                            <div className="h-[1px] w-4 bg-[#FF4D00]/20"></div>
                            <p className="text-blue-500 font-bold text-[10px] uppercase tracking-[0.25em]">{displayRole}</p>
                            <div className="h-[1px] w-4 bg-[#FF4D00]/20"></div>
                        </div>

                        <div className="space-y-4 text-left">
                            <div className="group/item flex items-center p-4 bg-zinc-900/30 rounded-2xl border border-white/5 hover:border-[#FF4D00]/20 transition-all duration-300">
                                <div className="p-2.5 bg-blue-500/10 rounded-xl text-blue-400 mr-4 transition-transform group-hover/item:scale-105">
                                    <Mail size={16} />
                                </div>
                                <div className="flex-1 overflow-hidden">
                                    <p className="label-text">Verified Email</p>
                                    <p className="value-text truncate">{user.email}</p>
                                </div>
                            </div>

                            <div className="group/item flex items-center p-4 bg-zinc-900/30 rounded-2xl border border-white/5 hover:border-[#FF4D00]/20 transition-all duration-300">
                                <div className="p-2.5 bg-emerald-500/10 rounded-xl text-emerald-400 mr-4 transition-transform group-hover/item:scale-105">
                                    <Phone size={16} />
                                </div>
                                <div className="flex-1">
                                    <p className="label-text">Mobile Access</p>
                                    <p className="value-text">{user.phone || '--'}</p>
                                </div>
                            </div>

                            <div className="group/item flex items-center p-4 bg-zinc-900/30 rounded-2xl border border-white/5 hover:border-[#FF4D00]/20 transition-all duration-300">
                                <div className="p-2.5 bg-purple-500/10 rounded-xl text-purple-400 mr-4 transition-transform group-hover/item:scale-105">
                                    <Shield size={16} />
                                </div>
                                <div className="flex-1">
                                    <p className="label-text">Identity Stat</p>
                                    <p className="value-text">Verified Secure</p>
                                </div>
                            </div>
                        </div>

                        <div className="mt-8">
                            <Button
                                onClick={onClose}
                                className="w-full bg-black text-white hover:bg-[#FF4D00] border border-white/10 hover:border-[#FF4D00] font-bold rounded-xl py-6 text-xs tracking-[0.2em] transition-all duration-500 shadow-xl"
                            >
                                CLOSE PROFILE
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    return createPortal(modalContent, document.body);
};

export default ProfileModal;
